import { useMemo } from 'react';
import { exerciseStaleness, type Staleness } from '@heraion/shared';

import {
  useEntryHistory,
  useEntryHistoryApiFetch,
} from './useEntryHistory';
import type { HistoryResult, ProposalContext } from './entryHistoryPick';
import { useExerciseSetsFallback } from './useExerciseSetsFallback';
import {
  lastPerformedAtMs,
  rowsFromLogSets,
  rowsFromSessions,
  HISTORY_SET_ROWS,
  type HistorySetRow,
} from './historySetRows';
import type { ProgramApiFetch } from '../ProgramEditor/types';

// LG-49 — THE answer to "what has this athlete done on this exercise", with
// the two doors folded into one so no surface has to know there are two.
//
// Order of asking, and why:
//   1. the cached session window — free, already in memory, right ~90% of the
//      time (an exercise trained this quarter is in there);
//   2. the per-exercise index — one gsi1 query, asked ONLY when the window
//      came back empty, which is exactly the case the window gets wrong.
//
// ⛔ Never both. The window answering is the common path and it must stay
// free; the index is the door you open when the first one is blind, not a
// belt-and-braces second fetch.
//
// ⭐ `resolved` is the whole point of the exercise (2026-08-25). Today the
// panel prints "No history yet" the moment the window has nothing, and that
// sentence is a LIE for any lift resumed after a few months. It may only be
// printed once something has actually said "none" — a served index answer.
// Until then the honest state is "still looking", not "nothing"
// ([[feedback_empty_vs_unresolved_collection]]).

// Never called: the fallback is gated on `needed`, which is false without a
// Provider. It exists so the hook below can run unconditionally, because a
// hook that runs on some renders and not others is a crash waiting for the
// first athlete who opens the logger outside the Provider.
export const NO_FETCH: ProgramApiFetch = () =>
  Promise.reject(new Error('entry history: no provider'));

export type ExerciseHistoryView = {
  ready: boolean;
  /** The window's own result — still the source for the similar-exercise
   *  estimate, which is unchanged and stays the LAST resort. */
  data: HistoryResult | null;
  /** Last five sets, newest first, from whichever door answered. */
  rows: HistorySetRow[];
  staleness: Staleness;
  /** True when "there is nothing" is a fact rather than a silence. */
  resolved: boolean;
  /** True while the index door is still being opened. */
  loading: boolean;
};

export function useExerciseHistoryView(
  exerciseId: string,
  nowMs: number = Date.now(),
  /** How many past sets to hand back. ⭐ LG-59: "più sedute" was his ask, and
   *  the right number depends on the surface (a bottom sheet and the exercise
   *  card have different room), so the mount decides. Defaults to the five
   *  `historySetRows` has always used, which keeps every existing caller — and
   *  every test that pins five — unchanged. */
  rowLimit: number = HISTORY_SET_ROWS,
  /** Unit and plate step the proposals are rounded with. Callers memo it. */
  proposalCtx?: ProposalContext,
): ExerciseHistoryView {
  const { ready, data, lastSessionAtMs } = useEntryHistory(
    exerciseId,
    proposalCtx,
  );
  const apiFetch = useEntryHistoryApiFetch();

  const windowRows = useMemo(
    () => (data ? rowsFromSessions(data.recent, rowLimit) : []),
    [data, rowLimit],
  );

  // Ask the index only when the window is BLIND — ready, asked about a real
  // slug, and holding nothing for it. `ready` matters: before the window
  // lands, "empty" is not an answer either, and firing then would make every
  // logger open pay for a query it did not need.
  const windowBlind =
    exerciseId !== '' && ready && apiFetch !== null && windowRows.length === 0;

  const fallback = useExerciseSetsFallback(
    exerciseId,
    windowBlind,
    apiFetch ?? NO_FETCH,
  );

  const indexRows = useMemo(
    () => (fallback.sets ? rowsFromLogSets(fallback.sets, rowLimit) : []),
    [fallback.sets, rowLimit],
  );

  const rows = windowRows.length > 0 ? windowRows : indexRows;

  const staleness = useMemo(
    () =>
      exerciseStaleness({
        lastExerciseAtMs: lastPerformedAtMs(rows),
        lastSessionAtMs,
        nowMs,
      }),
    [rows, lastSessionAtMs, nowMs],
  );

  return {
    ready,
    data,
    rows,
    staleness,
    // The window finding something IS an answer. Otherwise only a served
    // index reply counts — an error or an unopened door does not.
    resolved: windowRows.length > 0 || (windowBlind && fallback.resolved),
    loading: windowBlind && fallback.loading,
  };
}
